// Cloudflare Workers API(roff-goods-api)を呼び出す関数群
// デプロイ後のURLに変わった場合はここを更新する

const API_BASE = 'https://roff-goods-api.hauolikii-shiichan.workers.dev';

export type ShippingType = 'yamato80' | 'yamato60' | 'clickpost';

export type Product = {
  id: string;
  name: string;
  price: number;
  description: string;
  category: string;
  imageUrl: string | null;
  images: string[];
  shippingType: ShippingType;
  published: boolean;
  slug: string;
  stock: number;
  size: string;
  sizeOptions: string[];
};

export async function fetchProducts(): Promise<Product[]> {
  const res = await fetch(`${API_BASE}/api/products`);
  if (!res.ok) throw new Error('商品情報の取得に失敗しました');
  const data = await res.json();
  return data.products as Product[];
}

// --- 管理画面用(要パスワード) ---

export async function adminFetchProducts(password: string): Promise<Product[]> {
  const res = await fetch(`${API_BASE}/api/admin/products`, {
    headers: { Authorization: `Bearer ${password}` },
  });
  if (res.status === 401) throw new Error('unauthorized');
  if (!res.ok) throw new Error('商品情報の取得に失敗しました');
  const data = await res.json();
  return data.products as Product[];
}

export type AdminProductUpdate = {
  price?: number;
  description?: string;
  category?: string;
  published?: boolean;
  stock?: number;
};

export async function adminUpdateProduct(password: string, slug: string, updates: AdminProductUpdate): Promise<void> {
  const res = await fetch(`${API_BASE}/api/admin/products/${encodeURIComponent(slug)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${password}` },
    body: JSON.stringify(updates),
  });
  if (res.status === 401) throw new Error('unauthorized');
  if (!res.ok) throw new Error('更新に失敗しました');
}

export type OrderItem = {
  name: string;
  quantity: number;
  unitPrice: number;
  subtotal: number;
};

export type Order = {
  id: number;
  orderNumber: string;
  status: string;
  lastName: string;
  firstName: string;
  email: string;
  phone: string;
  zip: string;
  prefecture: string;
  city: string;
  address: string;
  apt: string | null;
  notes: string | null;
  subtotal: number;
  shippingFee: number;
  shippingMethod: string;
  total: number;
  paymentProvider: string;
  univapayChargeId: string | null;
  trackingNumber: string | null;
  paidAt: string | null;
  createdAt: string;
  items: OrderItem[];
};

export type OrdersSummary = {
  totalOrders: number;
  paidOrders: number;
  totalRevenue: number;
};

export async function adminFetchOrders(password: string): Promise<{ orders: Order[]; summary: OrdersSummary }> {
  const res = await fetch(`${API_BASE}/api/admin/orders`, {
    headers: { Authorization: `Bearer ${password}` },
  });
  if (res.status === 401) throw new Error('unauthorized');
  if (!res.ok) throw new Error('注文情報の取得に失敗しました');
  return res.json();
}

export async function adminUpdateOrderStatus(password: string, orderId: number, status: string, trackingNumber?: string): Promise<void> {
  const res = await fetch(`${API_BASE}/api/admin/orders/${orderId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${password}` },
    body: JSON.stringify({ status, trackingNumber }),
  });
  if (res.status === 401) throw new Error('unauthorized');
  if (!res.ok) throw new Error('更新に失敗しました');
}

export async function adminDeleteOrder(password: string, orderId: number): Promise<void> {
  const res = await fetch(`${API_BASE}/api/admin/orders/${orderId}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${password}` },
  });
  if (res.status === 401) throw new Error('unauthorized');
  if (!res.ok) throw new Error('削除に失敗しました');
}

export type ManualOrderPayload = {
  items: { slug: string; quantity: number }[];
  paymentMethod: string;
  customerName?: string;
  notes?: string;
};

export async function adminCreateManualOrder(password: string, payload: ManualOrderPayload): Promise<{ ok: boolean; orderNumber?: string; error?: string }> {
  const res = await fetch(`${API_BASE}/api/admin/orders/manual`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${password}` },
    body: JSON.stringify(payload),
  });
  if (res.status === 401) throw new Error('unauthorized');
  return res.json();
}

export type PaymentMethod = 'card' | 'bank_transfer';

export type CheckoutPayload = {
  items: { slug: string; quantity: number; size?: string }[];
  paymentMethod: PaymentMethod;
  transactionTokenId?: string;
  recipient: {
    lastName: string;
    firstName: string;
    email: string;
    phone: string;
    zip: string;
    prefecture: string;
    city: string;
    address: string;
    apt?: string;
  };
  notes?: string;
};

export type CheckoutResult = {
  ok?: boolean;
  orderNumber?: string;
  total?: number;
  shippingFee?: number;
  paymentMethod?: PaymentMethod;
  warning?: string;
  error?: string;
  detail?: string;
};

export async function submitCheckout(payload: CheckoutPayload): Promise<CheckoutResult> {
  const res = await fetch(`${API_BASE}/api/checkout`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return res.json();
}

export type ContactPayload = {
  name: string;
  email: string;
  message: string;
};

export type ContactResult = {
  ok?: boolean;
  error?: string;
};

export async function submitContact(payload: ContactPayload): Promise<ContactResult> {
  const res = await fetch(`${API_BASE}/api/contact`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return res.json();
}
